import { useState, useEffect, useCallback } from "react";
import { useAuth, API } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { 
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow 
} from "../components/ui/table";
import { ScrollArea } from "../components/ui/scroll-area";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { 
  Clock, CheckCircle2, XCircle, Pause, Play, RotateCcw,
  ChevronLeft, ChevronRight, Loader2, Eye
} from "lucide-react";

const PipelineRunsPage = () => {
  const { token } = useAuth();
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [statusFilter, setStatusFilter] = useState("");
  const [selectedRun, setSelectedRun] = useState(null);
  const [runLogs, setRunLogs] = useState([]);
  const [runErrors, setRunErrors] = useState([]);
  const limit = 20;

  const fetchRuns = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API}/pipeline/runs?limit=${limit}&offset=${offset}`;
      if (statusFilter) url += `&status=${statusFilter}`;
      
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setRuns(data.runs || []);
        setTotal(data.total || 0);
      }
    } catch (error) {
      console.error("Failed to fetch runs:", error);
    } finally {
      setLoading(false);
    }
  }, [token, offset, statusFilter]);

  useEffect(() => {
    fetchRuns();
  }, [fetchRuns]);

  const fetchRunDetails = async (runId) => {
    try {
      const [logsRes, errorsRes] = await Promise.all([
        fetch(`${API}/pipeline/runs/${runId}/logs?limit=100`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`${API}/pipeline/runs/${runId}/errors`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      
      if (logsRes.ok) {
        const data = await logsRes.json();
        setRunLogs(data.logs || []);
      }
      if (errorsRes.ok) {
        const data = await errorsRes.json();
        setRunErrors(data.errors || []);
      }
    } catch (error) {
      console.error("Failed to fetch run details:", error);
    }
  };

  const openRunDetails = (run) => {
    setSelectedRun(run);
    fetchRunDetails(run.run_id);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    // Ensure ISO string is treated as UTC by appending Z if missing
    const utcDateStr = dateStr.endsWith('Z') ? dateStr : dateStr + 'Z';
    return new Date(utcDateStr).toLocaleString();
  };

  const formatDuration = (start, end) => {
    if (!start) return "—";
    // Ensure ISO strings are treated as UTC
    const startStr = start.endsWith('Z') ? start : start + 'Z';
    const endStr = end ? (end.endsWith('Z') ? end : end + 'Z') : null;
    
    const startDate = new Date(startStr);
    const endDate = endStr ? new Date(endStr) : new Date();
    
    const diffMs = endDate.getTime() - startDate.getTime();
    if (diffMs < 0) return "0m 0s"; // Guard against negative durations
    
    const seconds = Math.floor(diffMs / 1000);
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m ${secs}s`;
  };

  const getStatusBadge = (status) => {
    const styles = {
      idle: "status-idle",
      running: "status-running",
      paused: "status-paused",
      completed: "status-completed",
      failed: "status-failed",
      cancelled: "status-failed",
    };
    return <Badge className={`${styles[status] || styles.idle} rounded-sm`}>{status}</Badge>;
  };

  const statusFilters = ["", "running", "paused", "completed", "failed", "cancelled"];

  return (
    <div className="space-y-6" data-testid="pipeline-runs-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Pipeline Runs</h1>
          <p className="text-muted-foreground text-sm mt-1">
            History of all pipeline executions
          </p>
        </div>
        <Button variant="outline" onClick={fetchRuns} className="rounded-sm">
          <RotateCcw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2">
        {statusFilters.map((status) => (
          <button
            key={status || "all"}
            onClick={() => { setStatusFilter(status); setOffset(0); }}
            className={`filter-chip ${statusFilter === status ? "active" : ""}`}
          >
            {status || "All"}
          </button>
        ))}
      </div>

      {/* Runs Table */}
      <Card className="bg-card border-border rounded-sm">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <Table className="data-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Run ID</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Trigger</TableHead>
                  <TableHead>Environment</TableHead>
                  <TableHead>Started</TableHead>
                  <TableHead>Duration</TableHead>
                  <TableHead>Funds</TableHead>
                  <TableHead>Holdings</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {runs.map((run) => (
                  <TableRow key={run.run_id}>
                    <TableCell className="font-mono text-xs">{run.run_id.slice(0, 8)}</TableCell>
                    <TableCell>{getStatusBadge(run.status)}</TableCell>
                    <TableCell className="capitalize">{run.trigger_type}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="rounded-sm">
                        {run.environment}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm">{formatDate(run.started_at)}</TableCell>
                    <TableCell className="font-mono text-sm">
                      {formatDuration(run.started_at, run.ended_at)}
                    </TableCell>
                    <TableCell className="font-mono">{run.counters?.funds_selected || 0}</TableCell>
                    <TableCell className="font-mono">{run.counters?.holdings_ingested || 0}</TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openRunDetails(run)}
                        data-testid={`view-run-${run.run_id.slice(0, 8)}`}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {runs.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                      No pipeline runs found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Showing {offset + 1} - {Math.min(offset + limit, total)} of {total}
        </p>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setOffset(Math.max(0, offset - limit))}
            disabled={offset === 0}
            className="rounded-sm"
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setOffset(offset + limit)}
            disabled={offset + limit >= total}
            className="rounded-sm"
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Run Details Dialog */}
      <Dialog open={!!selectedRun} onOpenChange={() => setSelectedRun(null)}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden bg-card border-border">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              Run Details
              <span className="font-mono text-sm text-muted-foreground">
                {selectedRun?.run_id}
              </span>
            </DialogTitle>
          </DialogHeader>
          
          {selectedRun && (
            <div className="space-y-4">
              {/* Run Info */}
              <div className="grid grid-cols-4 gap-4">
                <div>
                  <p className="label-xs">Status</p>
                  {getStatusBadge(selectedRun.status)}
                </div>
                <div>
                  <p className="label-xs">Trigger</p>
                  <p className="text-sm capitalize">{selectedRun.trigger_type}</p>
                </div>
                <div>
                  <p className="label-xs">Config Version</p>
                  <p className="text-sm font-mono">v{selectedRun.config_version}</p>
                </div>
                <div>
                  <p className="label-xs">Duration</p>
                  <p className="text-sm font-mono">
                    {formatDuration(selectedRun.started_at, selectedRun.ended_at)}
                  </p>
                </div>
              </div>

              {/* Counters */}
              <div className="grid grid-cols-4 gap-4 p-4 bg-muted/30 rounded-sm">
                <div>
                  <p className="text-xs text-muted-foreground">Funds Selected</p>
                  <p className="font-mono text-lg">{selectedRun.counters?.funds_selected || 0}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Tasks Completed</p>
                  <p className="font-mono text-lg">{selectedRun.counters?.tasks_completed || 0}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Holdings Ingested</p>
                  <p className="font-mono text-lg">{selectedRun.counters?.holdings_ingested || 0}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Errors</p>
                  <p className="font-mono text-lg text-red-400">{runErrors.length}</p>
                </div>
              </div>

              {/* Logs */}
              <div>
                <p className="label-xs mb-2">Logs</p>
                <ScrollArea className="h-[200px] border border-border rounded-sm p-2">
                  {runLogs.map((log, i) => (
                    <div key={i} className={`log-entry ${log.level}`}>
                      <span className="opacity-50">{new Date(log.timestamp).toLocaleTimeString()}</span>
                      {" "}<span className="font-medium">[{log.level}]</span>
                      {" "}{log.message}
                    </div>
                  ))}
                  {runLogs.length === 0 && (
                    <p className="text-center py-4 text-muted-foreground text-sm">No logs</p>
                  )}
                </ScrollArea>
              </div>

              {/* Errors */}
              {runErrors.length > 0 && (
                <div>
                  <p className="label-xs mb-2 text-red-400">Errors</p>
                  <ScrollArea className="h-[150px] border border-red-500/20 rounded-sm p-2">
                    {runErrors.map((err, i) => (
                      <div key={i} className="text-sm text-red-400 py-1 border-b border-border last:border-0">
                        <span className="font-mono text-xs opacity-50">{err.stage}</span>
                        {" "}{err.message}
                      </div>
                    ))}
                  </ScrollArea>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PipelineRunsPage;
